import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Float, Cylinder, Octahedron } from '@react-three/drei';
import * as THREE from 'three';
import { TILE_SIZE, FLOOR_HEIGHT } from './constants';
import { SparkleParticles } from './shared/Particles3D';
import type { Position } from '@/types/game';

interface Collectible3DProps {
  position: Position;
  type: 'coin' | 'gem';
  totalCols: number;
  totalRows: number;
}

function CoinCollectible({ baseY }: { baseY: number }) {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((_, delta) => {
    if (groupRef.current) groupRef.current.rotation.y += delta * 2;
  });

  return (
    <Float speed={4} rotationIntensity={0} floatIntensity={0.5}>
      <group ref={groupRef} position={[0, baseY, 0]}>
        <Cylinder args={[0.15, 0.15, 0.05, 16]} rotation={[Math.PI / 2, 0, 0]} castShadow>
          <meshStandardMaterial color="#ffcc00" metalness={1} roughness={0.1} />
        </Cylinder>
        <Cylinder args={[0.1, 0.1, 0.06, 16]} rotation={[Math.PI / 2, 0, 0]} castShadow>
          <meshStandardMaterial color="#d4ac0d" metalness={1} roughness={0.3} />
        </Cylinder>
      </group>
      <group position={[0, baseY, 0]}>
        <SparkleParticles count={15} bounds={[0.6, 0.6, 0.6] as [number, number, number]} color="#ffff00" />
      </group>
    </Float>
  );
}

function GemCollectible({ baseY }: { baseY: number }) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((_, delta) => {
    if (meshRef.current) meshRef.current.rotation.y += delta;
  });

  return (
    <Float speed={3} rotationIntensity={0.5} floatIntensity={0.6}>
      <Octahedron ref={meshRef} args={[0.14, 0]} position={[0, baseY, 0]} castShadow>
        <meshPhysicalMaterial
          color="#cc22ff"
          transmission={0.9}
          opacity={1}
          roughness={0}
          ior={2.0}
          thickness={0.5}
        />
      </Octahedron>
      <group position={[0, baseY, 0]}>
        <SparkleParticles count={15} bounds={[0.6, 0.6, 0.6] as [number, number, number]} color="#e0b0ff" />
      </group>
      <pointLight
        position={[0, baseY, 0]}
        color="#cc22ff"
        intensity={1.5}
        distance={2}
      />
    </Float>
  );
}

export default function Collectible3D({ position, type, totalCols, totalRows }: Collectible3DProps) {
  const x = (position[0] - (totalCols - 1) / 2) * TILE_SIZE;
  const z = (position[1] - (totalRows - 1) / 2) * TILE_SIZE;
  const baseY = FLOOR_HEIGHT + 0.35;

  return (
    <group position={[x, 0, z]}>
      {type === 'coin' ? (
        <CoinCollectible baseY={baseY} />
      ) : (
        <GemCollectible baseY={baseY} />
      )}
      {type === 'coin' && (
        <pointLight position={[0, baseY, 0]} color="#FFD700" intensity={0.3} distance={1} />
      )}
    </group>
  );
}
